import geojsonvt from "geojson-vt";
import { fromGeojsonVt } from "@maplibre/vt-pbf";

export const SEMANTICS_OVERLAY_ZOOM = 15;

/**
 * Semantics Map Protocol adds a new URI scheme "sem://" to MapLibre.
 * It allows loading tile per tile metadata from semantics API, seamlessly.
 * You can use them with URI like:
 * - `sem://"semantics.osm|traffic_sign" IS NOT NULL/{z}/{x}/{y}`
 * - `sem://"semantics.osm|traffic_sign"='yes'/{z}/{x}/{y}`
 * @class Panoramax.utils.SemanticsMapProtocol
 * @typicalname semMapProtocol
 * @param {Panoramax.utils.API} api The API
 */
export default class SemanticsMapProtocol {
	constructor(api) {
		this.tiles = new Map();
		this.api = api;
	}

	/**
	 * Transforms a XYZ tile coordinates into GPS bounding box coordinates
	 * @private 
	 */
	_tileToBbox(x, y, z) {
		const n = Math.pow(2, z);
		const lon1 = x / n * 360 - 180;
		const lon2 = (x + 1) / n * 360 - 180;
		const lat1 = Math.atan(Math.sinh(Math.PI * (1 - 2 * y / n))) * 180 / Math.PI;
		const lat2 = Math.atan(Math.sinh(Math.PI * (1 - 2 * (y + 1) / n))) * 180 / Math.PI;
		return [lon1, lat2, lon2, lat1];
	}

	/**
	 * Merge and override semantics attribute to get a flat tags object.
	 * @private
	 */
	_semanticsToBasicProps(f) {
		const tags = {};

		// Set or append function
		const setOrAppend = kv => {
			if(tags[kv.key]) {
				tags[kv.key].add(kv.value);
			}
			else {
				tags[kv.key] = new Set([kv.value]);
			}
		};

		// Read all level of semantics
		(f.properties?.collection?.semantics || []).forEach(setOrAppend);
		(f.properties?.semantics || []).forEach(setOrAppend);
		(f.properties?.annotations || []).forEach(a => a.semantics.forEach(setOrAppend));

		Object.keys(tags).forEach(k => tags[k] = [...tags[k]].join(";"));

		// Add Panoramax IDs as well
		tags.picid = f.id;
		tags.seqid = f.collection;

		return tags;
	}

	/**
	 * Get a single tile
	 * @param {object} params MapLibre parameters 
	 * @param {AbortController} abort The abort signal handler
	 * @returns {object} The tile, as expected by MapLibre
	 * @memberof Panoramax.utils.SemanticsMapProtocol#
	 */
	tile() {
		return async (params, abort) => {
			abort = abort || new AbortController();
			const match = params.url.match(/^sem:\/\/\/(.+)\/(\d+)\/(\d+)\/(\d+)/);
			if (!match) throw new Error("Invalid URL");

			// Look for cache
			if(this.tiles.has(params.url)) {
				return { data: this.tiles.get(params.url).buffer.slice(0) };
			}
		
			// eslint-disable-next-line no-unused-vars
			const [_, f, z, x, y] = match;
		
			// Download from API
			const bbox = this._tileToBbox(parseInt(x), parseInt(y), parseInt(z));
			const url = `${this.api._endpoint}/search?bbox=${bbox.join(",")}&limit=1000&filter=${encodeURIComponent(f)}`;
			return fetch(url, { signal: abort.signal })
				.then(res => res.json())
				.then(geojson => {
					if(geojson.features) {
						geojson.type = "FeatureCollection";

						// Handle empty results
						if(geojson.features.length === 0) {
							const mvt = new Uint8Array();
							this.tiles.set(params.url, mvt);
							return { data: mvt.buffer.slice(0) };
						}

						// Remove useless stuff
						geojson.features = geojson.features.map(f => ({
							type: f.type,
							geometry: f.geometry,
							id: f.id,
							properties: this._semanticsToBasicProps(f)
						}));
					}

					// Transform GeoJSON into MVT
					const tileIndex = geojsonvt(geojson, {maxZoom: SEMANTICS_OVERLAY_ZOOM});
					const tile = tileIndex.getTile(z, x, y);
					if(!tile) { throw new Error("Can't create tile"); }
					const mvt = fromGeojsonVt({ layer: tile }, { version: 2 });
					this.tiles.set(params.url, mvt);
					return { data: mvt.buffer.slice(0) };
				});
		};
	}
}